import React from "react";
import BackgroundRings from "../components/BackgroundRings";
import FadeIn from "../components/FadeIn";
import { EMAIL, PHONE_DISPLAY, INSTAGRAM } from "../lib/constants";

const SectionHeading = ({ children }) => (
    <h2
        className="font-serif"
        style={{
            color: "#ffffff",
            fontSize: "clamp(22px, 2.6vw, 30px)",
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
            marginTop: 56,
            marginBottom: 18,
        }}
    >
        {children}
    </h2>
);

const Body = ({ children }) => (
    <p
        style={{
            color: "#bbb",
            fontSize: 16,
            lineHeight: 1.8,
            fontWeight: 300,
            margin: "0 0 14px",
        }}
    >
        {children}
    </p>
);

const Privacy = () => {
    return (
        <main data-testid="privacy-page">
            <section
                style={{
                    position: "relative",
                    paddingTop: 160,
                    paddingBottom: 60,
                    paddingLeft: 32,
                    paddingRight: 32,
                    overflow: "hidden",
                }}
                data-testid="privacy-hero"
            >
                <BackgroundRings opacity={0.05} position="right" />
                <div
                    style={{
                        maxWidth: 900,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 24 }}>
                            Privacy Policy
                        </p>
                        <h1
                            className="font-serif"
                            data-testid="privacy-headline"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(40px, 6vw, 72px)",
                                fontWeight: 400,
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                marginBottom: 24,
                            }}
                        >
                            Privacy Policy.
                        </h1>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 14,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                margin: 0,
                            }}
                        >
                            Last updated: 14 May 2026
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section
                style={{
                    padding: "40px 32px 120px",
                }}
            >
                <div style={{ maxWidth: 900, margin: "0 auto" }}>
                    <FadeIn>
                        <Body>
                            This Privacy Policy explains how The Reset
                            Sanctuary ("we", "us", "our") collects, uses and
                            protects information you provide when interacting
                            with this website (theresetsanctuary.org), our
                            WhatsApp channel, our Instagram (@resetsanctuaryofficial),
                            and any advertising campaigns we run on Meta
                            (Facebook, Instagram) or Google. By using our
                            services, you agree to the practices described
                            below.
                        </Body>

                        <SectionHeading>1. Who We Are</SectionHeading>
                        <Body>
                            The Reset Sanctuary is a wellness and recovery
                            practice operating in the United Arab Emirates,
                            with locations in Al Hamra, Ras Al Khaimah and Al
                            Furjan, Dubai, and in-villa sessions available
                            UAE-wide. We provide complementary wellness
                            sessions through precision sound and frequency
                            work. We do not diagnose, treat, or cure medical
                            conditions.
                        </Body>

                        <SectionHeading>
                            2. Information We Collect
                        </SectionHeading>
                        <Body>
                            We only collect information you voluntarily
                            provide, including:
                        </Body>
                        <Body>
                            • <strong style={{ color: "#fff" }}>Contact details</strong>{" "}
                            (name, phone number, email address, location) when
                            you reach out via WhatsApp, email, Instagram, or
                            submit a lead form through Meta or Google
                            advertising.
                        </Body>
                        <Body>
                            • <strong style={{ color: "#fff" }}>Session-related information</strong>{" "}
                            you choose to share with us — for example, the
                            reason for your booking (sleep, anxiety, athletic
                            recovery, etc.) — solely to tailor the session to
                            your needs.
                        </Body>
                        <Body>
                            • <strong style={{ color: "#fff" }}>Basic website analytics</strong>{" "}
                            (anonymised page views, device type, browser, and
                            general geographic region) collected automatically
                            through cookies and analytics tools to understand
                            how visitors use the site.
                        </Body>
                        <Body>
                            We do not knowingly collect information from
                            children under the age of 13. Where sessions
                            involve minors (e.g. Kids Sessions), bookings are
                            made by the parent or legal guardian.
                        </Body>

                        <SectionHeading>
                            3. How We Use Your Information
                        </SectionHeading>
                        <Body>
                            We use the information you provide only for the
                            following purposes:
                        </Body>
                        <Body>
                            • To respond to your enquiry and confirm your
                            session booking.
                        </Body>
                        <Body>
                            • To tailor the frequency profile and approach for
                            your session.
                        </Body>
                        <Body>
                            • To send you appointment-related messages via
                            WhatsApp or email.
                        </Body>
                        <Body>
                            • To improve the website, our services, and our
                            advertising campaigns.
                        </Body>
                        <Body>
                            • To comply with legal obligations under
                            applicable UAE law, including the UAE Personal
                            Data Protection Law (Federal Decree-Law No. 45 of
                            2021).
                        </Body>
                        <Body>
                            We do not sell, rent, or trade your personal
                            information to any third party for marketing
                            purposes.
                        </Body>

                        <SectionHeading>
                            4. Lead Forms via Meta (Facebook & Instagram) and
                            Google
                        </SectionHeading>
                        <Body>
                            If you submit your contact details through a Meta
                            Lead Ad or a Google Lead Form, that information is
                            transmitted to us by the respective platform under
                            their own terms of service. We use those details
                            exclusively to contact you about The Reset
                            Sanctuary's sessions and offerings. You may
                            request deletion of those details at any time
                            (see Section 8).
                        </Body>

                        <SectionHeading>
                            5. How We Share Information
                        </SectionHeading>
                        <Body>
                            We share your information only with:
                        </Body>
                        <Body>
                            • Our internal team members (founders and
                            practitioners) directly involved in delivering
                            your session.
                        </Body>
                        <Body>
                            • Service providers that help us operate the
                            website and respond to enquiries (e.g. hosting,
                            email, WhatsApp Business). These providers are
                            bound by their own privacy and security
                            obligations.
                        </Body>
                        <Body>
                            • Where required by law, court order, or to
                            protect our legal rights.
                        </Body>

                        <SectionHeading>6. Cookies, Analytics &amp; Advertising</SectionHeading>
                        <Body>
                            Our website uses essential cookies to function
                            correctly and analytics cookies to understand
                            visitor behaviour in an aggregated, anonymised
                            way.
                        </Body>
                        <Body>
                            We also use the{" "}
                            <strong style={{ color: "#fff" }}>
                                Meta Pixel
                            </strong>{" "}
                            (operated by Meta Platforms, Inc.) to measure the
                            performance of our advertising on Facebook and
                            Instagram, to deliver more relevant ads, and to
                            build retargeting and look-alike audiences. The
                            Meta Pixel collects information such as the pages
                            you visit, actions you take on our site (for
                            example submitting an email or initiating a
                            WhatsApp conversation), your IP address, your
                            browser and device information, and your general
                            location. This information is sent to Meta and is
                            handled under Meta&apos;s own{" "}
                            <a
                                href="https://www.facebook.com/about/privacy/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: "#c9a227",
                                    textDecoration: "underline",
                                }}
                            >
                                Data Policy
                            </a>
                            .
                        </Body>
                        <Body>
                            You can opt out of personalised advertising at
                            any time by adjusting your{" "}
                            <a
                                href="https://www.facebook.com/adpreferences/ad_settings"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: "#c9a227",
                                    textDecoration: "underline",
                                }}
                            >
                                Facebook Ad Preferences
                            </a>
                            , by using{" "}
                            <a
                                href="https://www.youronlinechoices.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: "#c9a227",
                                    textDecoration: "underline",
                                }}
                            >
                                YourOnlineChoices.com
                            </a>{" "}
                            (for EU/UK residents), or by disabling
                            third-party cookies in your browser. Some parts
                            of the site may not function properly if cookies
                            are disabled.
                        </Body>

                        <SectionHeading>7. Data Retention</SectionHeading>
                        <Body>
                            We retain personal information only for as long
                            as needed to provide our services and to comply
                            with legal obligations. WhatsApp conversations
                            are retained on Meta's infrastructure under
                            Meta's own retention policies. We typically
                            review and purge inactive contact records every
                            24 months.
                        </Body>

                        <SectionHeading>8. Your Rights</SectionHeading>
                        <Body>
                            Under UAE PDPL and equivalent international
                            frameworks (such as GDPR for visitors from the
                            EU/UK), you have the right to:
                        </Body>
                        <Body>
                            • Access the personal information we hold about
                            you.
                        </Body>
                        <Body>
                            • Request correction of inaccurate or incomplete
                            information.
                        </Body>
                        <Body>
                            • Request deletion of your information ("right to
                            be forgotten").
                        </Body>
                        <Body>
                            • Withdraw consent at any time for any
                            future-facing processing.
                        </Body>
                        <Body>
                            • Lodge a complaint with the UAE Data Office or
                            your local data protection authority.
                        </Body>
                        <Body>
                            To exercise any of these rights, contact us using
                            the details in Section 11. We will respond within
                            30 days.
                        </Body>

                        <SectionHeading>9. Data Security</SectionHeading>
                        <Body>
                            We take reasonable technical and organisational
                            measures to protect your information against
                            loss, misuse, and unauthorised access. No method
                            of transmission over the internet is 100% secure,
                            but we work with trusted infrastructure providers
                            and limit access to your information on a
                            need-to-know basis.
                        </Body>

                        <SectionHeading>
                            10. Children & Vulnerable Persons
                        </SectionHeading>
                        <Body>
                            Sessions involving children, animals, or
                            vulnerable persons are arranged through a parent,
                            legal guardian, or owner. We do not knowingly
                            collect data from children under 13 directly.
                        </Body>

                        <SectionHeading>11. Contact Us</SectionHeading>
                        <Body>
                            For any privacy-related question, complaint, or
                            request:
                        </Body>
                        <Body>
                            • <strong style={{ color: "#fff" }}>Email:</strong>{" "}
                            <a
                                href={`mailto:${EMAIL}`}
                                style={{
                                    color: "#c9a227",
                                    textDecoration: "none",
                                    borderBottom:
                                        "1px solid rgba(201,162,39,0.4)",
                                }}
                                data-testid="privacy-email-link"
                            >
                                {EMAIL}
                            </a>
                        </Body>
                        <Body>
                            • <strong style={{ color: "#fff" }}>WhatsApp:</strong>{" "}
                            {PHONE_DISPLAY}
                        </Body>
                        <Body>
                            • <strong style={{ color: "#fff" }}>Instagram:</strong>{" "}
                            {INSTAGRAM}
                        </Body>

                        <SectionHeading>
                            12. Changes to This Policy
                        </SectionHeading>
                        <Body>
                            We may update this Privacy Policy from time to
                            time. The "Last updated" date at the top of this
                            page indicates when the most recent revision was
                            made. Material changes will be highlighted on the
                            website or communicated through WhatsApp or
                            email.
                        </Body>

                        <p
                            style={{
                                color: "#666",
                                fontSize: 13,
                                lineHeight: 1.7,
                                fontWeight: 300,
                                marginTop: 64,
                                paddingTop: 24,
                                borderTop:
                                    "1px solid rgba(201,162,39,0.15)",
                            }}
                            data-testid="privacy-disclaimer"
                        >
                            The Reset Sanctuary offers wellness and recovery
                            sessions through precision sound and frequency
                            work. We do not diagnose, treat, or cure medical
                            conditions. Our services are complementary
                            wellness practices, not medical care. Please
                            consult a licensed healthcare provider for
                            medical concerns.
                        </p>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Privacy;
